import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

export interface AuthUser {
  id: number;
  username: string;
  email: string | null;
  display_name: string;
  role: string;
  profile_image: string | null;
  avatar_type: string;
  avatar_icon: string | null;
  nav_items: string | null;
  push_enabled: boolean;
  push_categories: string | null;
  bell_categories: string | null;
  last_login_at: string | null;
  permissions: Record<string, string>;
}

const TOKEN_KEY = 'wendler_token';
const USER_KEY  = 'wendler_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _user = signal<AuthUser | null>(this.loadUser());

  readonly user = this._user.asReadonly();

  constructor(private http: HttpClient, private router: Router) {}

  login(username: string, password: string) {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, { username, password }).pipe(
      tap(res => {
        localStorage.setItem(TOKEN_KEY, res.access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(res.user));
        this._user.set(res.user);
      })
    );
  }

  /** Aktuelle Nutzerdaten inkl. Berechtigungen vom Server holen (z.B. beim App-Start),
   *  damit Rechte-Änderungen durch den Admin sofort greifen. */
  refreshMe() {
    return this.http.get<AuthUser>(`${environment.apiUrl}/auth/me`).pipe(
      tap(user => this.updateUser(user))
    );
  }

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._user.set(null);
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  }

  isAdmin(): boolean {
    return this._user()?.role === 'ADMIN';
  }

  /** Prüft, ob der Nutzer READ/WRITE auf einem Modul hat. ADMIN immer true. */
  can(module: string, action: 'READ' | 'WRITE' = 'READ'): boolean {
    if (this.isAdmin()) return true;
    const have = this._user()?.permissions?.[module] ?? 'NONE';
    const rank: Record<string, number> = { NONE: 0, READ: 1, WRITE: 2 };
    return (rank[have] ?? 0) >= rank[action];
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  updateUser(user: AuthUser) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this._user.set(user);
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      const u = JSON.parse(raw) as AuthUser;
      if (!u.permissions) u.permissions = {};
      return u;
    } catch {
      return null;
    }
  }
}
